<template>
  <div class="container py-4">
    <div class="page-card">
      <h4 class="mb-3">Accounting</h4>
      <b-tabs>
        <b-tab title="Chart of accounts" active>
          <b-table
            class="mt-3"
            :items="accounts"
            :fields="accountFields"
            small
            responsive
          />
        </b-tab>
        <b-tab title="Trial balance">
          <div v-if="trialBalance" class="mt-3">
            <p class="text-muted small mb-3">
              As of {{ asOfLabel }} —
              <span :class="isBalanced ? 'text-success' : 'text-danger'">
                {{ isBalanced ? "Balanced" : "Out of balance" }}
              </span>
            </p>

            <div class="tb-summary" role="group" aria-label="Trial balance totals">
              <div class="tb-summary__item">
                <span class="tb-summary__label">Total DR</span>
                <span class="tb-summary__value">{{ formatMoney(totalDebit) }}</span>
              </div>
              <div class="tb-summary__item">
                <span class="tb-summary__label">Total CR</span>
                <span class="tb-summary__value">{{ formatMoney(totalCredit) }}</span>
              </div>
              <div
                class="tb-summary__item"
                :class="isBalanced ? 'tb-summary__item--ok' : 'tb-summary__item--warn'"
              >
                <span class="tb-summary__label">Difference</span>
                <span class="tb-summary__value">{{ formatMoney(difference) }}</span>
              </div>
            </div>

            <b-table
              :items="trialBalance.accounts"
              :fields="tbFields"
              small
              responsive
            >
              <template #cell(debit)="data">{{ formatMoney(data.value) }}</template>
              <template #cell(credit)="data">{{ formatMoney(data.value) }}</template>
              <template #cell(balance)="data">{{ formatMoney(data.value) }}</template>
              <template #custom-foot>
                <b-tr class="tb-foot">
                  <b-th colspan="2">Totals</b-th>
                  <b-th class="text-end">{{ formatMoney(totalDebit) }}</b-th>
                  <b-th class="text-end">{{ formatMoney(totalCredit) }}</b-th>
                  <b-th class="text-end">{{ formatMoney(difference) }}</b-th>
                </b-tr>
              </template>
            </b-table>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";

export default {
  name: "Accounting",
  data() {
    return {
      accounts: [],
      trialBalance: null,
      accountFields: [
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "type", label: "Type" },
      ],
      tbFields: [
        { key: "accountCode", label: "Code" },
        { key: "accountName", label: "Name" },
        { key: "debit", label: "Debit", class: "text-end", thClass: "text-end" },
        { key: "credit", label: "Credit", class: "text-end", thClass: "text-end" },
        { key: "balance", label: "Balance", class: "text-end", thClass: "text-end" },
      ],
    };
  },
  computed: {
    totalDebit() {
      if (this.trialBalance?.totalDebit != null) {
        return Number(this.trialBalance.totalDebit);
      }
      return (this.trialBalance?.accounts || []).reduce(
        (sum, row) => sum + Number(row.debit || 0),
        0,
      );
    },
    totalCredit() {
      if (this.trialBalance?.totalCredit != null) {
        return Number(this.trialBalance.totalCredit);
      }
      return (this.trialBalance?.accounts || []).reduce(
        (sum, row) => sum + Number(row.credit || 0),
        0,
      );
    },
    difference() {
      if (this.trialBalance?.difference != null) {
        return Number(this.trialBalance.difference);
      }
      return Math.round((this.totalDebit - this.totalCredit) * 100) / 100;
    },
    isBalanced() {
      return Math.round(this.difference * 100) === 0;
    },
    asOfLabel() {
      const raw = this.trialBalance?.asOfDate;
      if (!raw) return "";
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return raw;
      return date.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.accounts = await ApiService.get("accounting/chart-of-accounts");
      this.trialBalance = await ApiService.get("accounting/trial-balance");
    },
  },
};
</script>

<style scoped>
.tb-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tb-summary__item {
  padding: 0.75rem 1rem;
  border: 1px solid var(--pos-border-subtle, #e2e8f0);
  border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-surface-muted, #f5f6f8);
}

.tb-summary__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pos-muted, #64748b);
  margin-bottom: 0.2rem;
}

.tb-summary__value {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.tb-summary__item--ok .tb-summary__value {
  color: var(--akkurate-green, #1a7f4e);
}

.tb-summary__item--warn {
  border-color: rgba(185, 28, 28, 0.25);
  background: rgba(185, 28, 28, 0.06);
}

.tb-summary__item--warn .tb-summary__value {
  color: #b91c1c;
}

.tb-foot > th {
  font-weight: 700;
  border-top: 2px solid var(--pos-border, #d1d5db);
}

@media (max-width: 768px) {
  .tb-summary {
    grid-template-columns: 1fr;
  }
}
</style>
